import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { IReportCountProgress } from 'src/app/interfaces/IReportCountProgress';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { ElectionService } from 'src/app/services/election.service';

@Component({
  selector: 'count-progress-table',
  templateUrl: './count-progress-table.component.html',
  styleUrls: ['./count-progress-table.component.scss'],
})
export class CountProgressTableComponent implements AfterViewInit, OnDestroy {
  @Input() district = '';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public title = '';
  public subTitle = '';
  public countProgress: IReportCountProgress | null = null;
  public boothWid = '10px';
  public evcWid = '10px';
  public dvWid = '10px';

  constructor(private electionService: ElectionService) {
    this.electionService.dataLoaded$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((dataLoaded) => {
        if (this.isDataLoaded(dataLoaded.electionDate)) {
          this.loadChart(this.electionService.selectedPage);
        }
      });
    this.electionService.pageChanged$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((page) => {
        this.loadChart(page);
      });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic,
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (
      this.isDataLoaded(
        this.electionService.selectedPage.electionDateString ?? ''
      )
    ) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private loadChart(page: ISelectPage): void {
    const election = this.electionService.electionMap.get(
      page.electionDateString ?? ''
    );
    const isFinal = election?.electionCount?.electionStatus === 'final';

    if (page.pageType === PageTypeEnum.CountHAStateWide) {
      this.countProgress = {
        boothsReported: election?.electionCount?.pollingBoothsWithCounts ?? 0,
        boothsPercent: election?.electionCount?.pollingBoothCountPercent ?? 0,
        boothsTotal: election?.electionCount?.totalPollingBooths ?? 0,
        earlyVotingCentresReported:
          election?.electionCount?.earlyVotingCentresWithCounts ?? 0,
        earlyVotingCentresPercent:
          election?.electionCount?.earlyVotingCentreCountPercent ?? 0,
        earlyVotingCentresTotal:
          election?.electionCount?.totalEarlyVotingCentres ?? 0,
        declarationVotesReported:
          election?.electionCount?.declarationTypesWithCounts ?? 0,
        declarationVotesPercent:
          election?.electionCount?.declarationTypeCountPercent ?? 0,
        declarationVotesTotal:
          election?.electionCount?.totalDeclarationTypes ?? 0,
        isFinal: isFinal,
      };
    }
    if (page.pageType === PageTypeEnum.CountHADistrict) {
      const foundDistrict = election?.electionCount?.getDistrict(
        page.countDistrict
      );
      if (foundDistrict) {
        this.countProgress = {
          boothsReported: foundDistrict.districtPollingBoothsWithCounts ?? 0,
          boothsPercent: foundDistrict.pollingBoothCountPercent ?? 0,
          boothsTotal: foundDistrict.districtPollingBooths ?? 0,
          earlyVotingCentresReported:
            foundDistrict.districtEarlyVotingCentresWithCounts ?? 0,
          earlyVotingCentresPercent:
            foundDistrict.earlyVotingCentreCountPercent ?? 0,
          earlyVotingCentresTotal:
            foundDistrict.districtEarlyVotingCentres ?? 0,
          declarationVotesReported:
            foundDistrict.districtDeclarationTypesWithCounts ?? 0,
          declarationVotesPercent:
            foundDistrict.declarationTypeCountPercent ?? 0,
          declarationVotesTotal: foundDistrict.districtDeclarationTypes ?? 0,
          isFinal: isFinal,
        };
      }
    }
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
